import React, { useState, useEffect } from 'react';

interface StreamingTextProps {
  text: string;
  speed?: number; // Characters per second
  onComplete?: () => void;
  onTextUpdate?: () => void; // Called on each character update
  startStreaming?: boolean;
  renderAs?: 'text' | 'markdown';
}

const StreamingText: React.FC<StreamingTextProps> = ({ 
  text, 
  speed = 150, 
  onComplete,
  onTextUpdate,
  startStreaming = true,
  renderAs = 'text'
}) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    if (!startStreaming) {
      setDisplayedText(text);
      setIsComplete(true);
      return;
    }

    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
        // Trigger scroll update after each character
        onTextUpdate?.();
      }, 1000 / speed);

      return () => clearTimeout(timer);
    } else if (!isComplete) {
      setIsComplete(true);
      onComplete?.();
    }
  }, [currentIndex, text, speed, onComplete, onTextUpdate, isComplete, startStreaming]);

  // Reset when text changes
  useEffect(() => {
    if (startStreaming) {
      setDisplayedText('');
      setCurrentIndex(0);
      setIsComplete(false);
    }
  }, [text, startStreaming]);

  if (renderAs === 'markdown') {
    return (
      <div className="streaming-markdown">
        {/* <CommonMarkRenderer content={displayedText} /> */}
        {!isComplete && startStreaming && (
          <span className="streaming-cursor">|</span>
        )}
      </div>
    );
  }

  return (
    <span style={{ whiteSpace: 'pre-wrap' }}
      dangerouslySetInnerHTML={{ 
        __html: (displayedText || '')
          .replace(/\\\\n/g, '<br />') // Handle double-escaped newlines
          .replace(/\\n/g, '<br />') // Handle escaped newlines
          .replace(/\n/g, '<br />') // Handle actual newlines
      }}
    />
  );
};

export default StreamingText;
